import { useEffect, useRef, useState } from "react";
import { CampaignType } from "@adlywise/shared";
import Card from "@/components/common/Card";
import Button from "@/components/common/Button";
import { STATUSES } from "./campaignFormUtils";

const PLAN_DOWNLOAD_STEPS = [
  "Analyzing historical data…",
  "Building the plan…",
  "Preparing the soc-dem info…",
  "Calculating the budget…",
];

const ReviewRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <dt className="text-(--text-secondary)">{label}</dt>
    <dd className="text-right font-medium text-(--text-primary)">
      {value === "" || value === null || value === undefined ? "—" : value}
    </dd>
  </div>
);

export default function Step5Review({
  form,
  segments,
  providers,
  creatives,
  segmentCreatives,
  isEditMode,
  campaignUuid,
  onDownloadPlan,
  onModerate,
  isModerateSaving,
  canApprove = false,
  onApproveAndLaunch,
  isApproving = false,
}) {
  const isTransit = form.type === CampaignType.Transit;
  const isPop = form.type === CampaignType.Popunder;
  const [isPlanDownloading, setIsPlanDownloading] = useState(false);
  const [planStepIndex, setPlanStepIndex] = useState(0);
  const planStepIntervalRef = useRef(null);

  useEffect(() => {
    if (!isPlanDownloading) {
      setPlanStepIndex(0);
      if (planStepIntervalRef.current) {
        clearInterval(planStepIntervalRef.current);
        planStepIntervalRef.current = null;
      }
      return undefined;
    }

    setPlanStepIndex(0);

    planStepIntervalRef.current = setInterval(() => {
      setPlanStepIndex((prev) => {
        const next = prev + 1;

        if (next >= PLAN_DOWNLOAD_STEPS.length - 1) {
          if (planStepIntervalRef.current) {
            clearInterval(planStepIntervalRef.current);
            planStepIntervalRef.current = null;
          }
          return PLAN_DOWNLOAD_STEPS.length - 1;
        }

        return next;
      });
    }, 10_000);

    return () => {
      if (planStepIntervalRef.current) {
        clearInterval(planStepIntervalRef.current);
        planStepIntervalRef.current = null;
      }
    };
  }, [isPlanDownloading]);

  const handleDownloadPlan = async () => {
    setIsPlanDownloading(true);
    try {
      await onDownloadPlan();
    } finally {
      setIsPlanDownloading(false);
    }
  };

  const segmentName = (uuid) =>
    segments.find((s) => s.uuid === uuid)?.name ??
    `Segment #${uuid.slice(0, 6)}`;
  const publisherName = (uuid) =>
    (Array.isArray(providers) ? providers : []).find((p) => p.uuid === uuid)
      ?.name ?? `Publisher #${uuid.slice(0, 6)}`;
  const creativeName = (uuid) => {
    const creative = creatives.find((c) => c.uuid === uuid);
    return creative ? creative.name || creative.url : uuid;
  };
  const statusLabel =
    STATUSES.find((s) => s.value === form.status)?.label ?? form.status;
  const assignedCreatives = Object.entries(segmentCreatives).filter(
    ([segUuid, creativeUuid]) =>
      creativeUuid && form.selectedSegments.includes(segUuid),
  );

  const isModerateSubmitDisabled =
    !campaignUuid || form.status !== "draft" || isModerateSaving;

  return (
    <Card className="flex flex-col gap-5">
      <div>
        <h3 className="text-base font-semibold text-(--text-primary)">
          Review your campaign
        </h3>
        <p className="mt-1 text-sm text-(--text-secondary)">
          Check all settings before {isEditMode ? "saving" : "creating"} the
          campaign. Use the stepper above to jump back and adjust.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-(--radius-md) border border-(--border-subtle) p-4">
          <h4 className="mb-3 text-sm font-semibold text-(--text-primary)">
            Basics
          </h4>
          <dl className="flex flex-col gap-2 text-sm">
            <ReviewRow label="Name" value={form.name} />
            <ReviewRow
              label="Type"
              value={isTransit ? "Transit" : isPop ? "Popunder" : "Standard"}
            />
            <ReviewRow label="Status" value={statusLabel} />
            <ReviewRow
              label="Flight dates"
              value={
                form.startDate || form.endDate
                  ? `${form.startDate || "…"} → ${form.endDate || "…"}`
                  : "—"
              }
            />
          </dl>
        </div>

        {isPop ? (
          <div className="rounded-(--radius-md) border border-(--border-subtle) p-4">
            <h4 className="mb-3 text-sm font-semibold text-(--text-primary)">
              Popunder
            </h4>
            <dl className="flex flex-col gap-2 text-sm">
              <ReviewRow label="Destination" value={form.pop.destination} />
              <ReviewRow
                label="Publishers"
                value={form.pop.selectedPublishers
                  .map(publisherName)
                  .join(", ")}
              />
              <ReviewRow label="Countries" value={form.pop.include || "Any"} />
              <ReviewRow
                label="Excluded regions"
                value={form.pop.excludeRegions || "—"}
              />
              <ReviewRow
                label="Frequency cap"
                value={`${form.pop.capCount} per ${form.pop.capWindowHours} h`}
              />
              <ReviewRow label="Margin" value={`${form.pop.marginPct}%`} />
              <ReviewRow
                label="Billable visit"
                value={
                  form.pop.confirmMode === "fingerprint"
                    ? "Fingerprint matches the bid"
                    : "Tracker landing postback"
                }
              />
            </dl>
          </div>
        ) : isTransit ? (
          <div className="rounded-(--radius-md) border border-(--border-subtle) p-4">
            <h4 className="mb-3 text-sm font-semibold text-(--text-primary)">
              Transit
            </h4>
            <dl className="flex flex-col gap-2 text-sm">
              <ReviewRow label="Entrypoint" value={form.metadata.entrypoint} />
              <ReviewRow
                label="Margin"
                value={
                  form.metadata.margin !== "" && form.metadata.margin != null
                    ? `${form.metadata.margin}%`
                    : "—"
                }
              />
              <ReviewRow label="Max outgoing QPS" value={form.metadata.qps} />
              <ReviewRow
                label="Timeout"
                value={
                  form.metadata.timeoutMs !== "" &&
                  form.metadata.timeoutMs != null
                    ? `${form.metadata.timeoutMs} ms`
                    : "Platform default"
                }
              />
              <ReviewRow
                label="Routing filters"
                value={(() => {
                  const active = Object.values(
                    form.metadata.filters ?? {},
                  ).filter((f) => f && f.mode !== "all" && f.values?.length);
                  return active.length > 0 ? `${active.length} active` : "None";
                })()}
              />
              <ReviewRow
                label="Publishers"
                value={
                  form.metadata.selectedPublishers.length > 0
                    ? form.metadata.selectedPublishers
                        .map(publisherName)
                        .join(", ")
                    : "—"
                }
              />
              <ReviewRow
                label="Block list"
                value={
                  form.metadata.blockList.length > 0
                    ? form.metadata.blockList.join(", ")
                    : "—"
                }
              />
              <ReviewRow
                label="Whitelist"
                value={
                  (form.metadata.whiteList ?? []).length > 0
                    ? form.metadata.whiteList.join(", ")
                    : "—"
                }
              />
            </dl>
          </div>
        ) : (
          <div className="rounded-(--radius-md) border border-(--border-subtle) p-4">
            <h4 className="mb-3 text-sm font-semibold text-(--text-primary)">
              Audience
            </h4>
            <dl className="flex flex-col gap-2 text-sm">
              <ReviewRow
                label="Audiences"
                value={
                  form.selectedSegments.length > 0
                    ? form.selectedSegments.map(segmentName).join(", ")
                    : "Everyone"
                }
              />
              {form.metadata.selectedPublishers.length > 0 && (
                <ReviewRow
                  label="Publishers"
                  value={form.metadata.selectedPublishers
                    .map(publisherName)
                    .join(", ")}
                />
              )}
            </dl>
          </div>
        )}

        {!isPop && (
          <div className="rounded-(--radius-md) border border-(--border-subtle) p-4">
            <h4 className="mb-3 text-sm font-semibold text-(--text-primary)">
              Creatives
            </h4>
            <dl className="flex flex-col gap-2 text-sm">
              <ReviewRow
                label="Default creative"
                value={
                  form.defaultCreativeId
                    ? creativeName(form.defaultCreativeId)
                    : "No default creative assigned"
                }
              />
              {assignedCreatives.length > 0 ? (
                assignedCreatives.map(([segUuid, creativeUuid]) => (
                  <ReviewRow
                    key={segUuid}
                    label={segmentName(segUuid)}
                    value={creativeName(creativeUuid)}
                  />
                ))
              ) : (
                <ReviewRow
                  label="Assigned"
                  value="No segment-level creatives assigned"
                />
              )}
              {form.impressionDomain && (
                <ReviewRow
                  label="Impression domain"
                  value={form.impressionDomain}
                />
              )}
            </dl>
          </div>
        )}

        {!isTransit && (
          <div className="rounded-(--radius-md) border border-(--border-subtle) p-4">
            <h4 className="mb-3 text-sm font-semibold text-(--text-primary)">
              Delivery & budget
            </h4>
            <dl className="flex flex-col gap-2 text-sm">
              <ReviewRow label="CPM" value={form.cpm ? `$${form.cpm}` : "—"} />
              <ReviewRow
                label="Budget cap"
                value={form.budgetCap ? `$${form.budgetCap}` : "—"}
              />
              {isPop ? (
                <ReviewRow
                  label="Confirmed visits cap"
                  value={form.impressionsCap || "—"}
                />
              ) : (
                <>
                  <ReviewRow label="Daily cap" value={form.dailyFrequency} />
                  <ReviewRow label="Weekly cap" value={form.weeklyFrequency} />
                  <ReviewRow
                    label="Impressions cap"
                    value={form.impressionsCap}
                  />
                </>
              )}
            </dl>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-(--border-subtle) pt-4">
        {/* The media plan is audience-based: not for popunder campaigns. */}
        {!isPop && (
          <Button
            variant="secondary"
            onClick={handleDownloadPlan}
            disabled={!isEditMode || isPlanDownloading}
          >
            {isPlanDownloading && (
              <span className="spinner spinner--sm" aria-hidden="true" />
            )}
            <span>
              {isPlanDownloading
                ? PLAN_DOWNLOAD_STEPS[planStepIndex]
                : "Download media plan"}
            </span>
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={onModerate}
          disabled={isModerateSubmitDisabled}
        >
          {isModerateSaving
            ? "Submitting for moderation…"
            : "Submit for moderation"}
        </Button>
        {canApprove && (
          <Button
            onClick={onApproveAndLaunch}
            disabled={isApproving || isModerateSaving}
          >
            {isApproving ? "Approving & launching…" : "Approve & launch"}
          </Button>
        )}
        {!isEditMode && (
          <p className="text-xs text-(--text-muted)">
            Media plan and moderation become available after the campaign is
            created.
          </p>
        )}
      </div>
    </Card>
  );
}
